import type { TerrainEditor, TerrainToolKind } from '@/editor/TerrainEditor';
import { MAX_SCULPT_STRENGTH, MIN_SCULPT_STRENGTH, sculptModeLabel } from '@/editor/TerrainEditor';
import { CONTOUR_LABEL_MODES, type ContourLabelMode } from '@/editor/ContourOverlay';
import { formatElevation } from '@/terrain/TerrainContours';
import type { UnitSystem } from '@/core/Units';
import {
  MAX_BRUSH_RADIUS,
  MIN_BRUSH_RADIUS,
} from '@/terrain/TerrainBrush';
import { SCULPT_MODES } from '@/terrain/TerrainSculpt';
import {
  surfaceLayers,
  surfacePalette,
  type SurfaceCoverage,
  type SurfaceLayer,
  type SurfaceLayerId,
} from '@/terrain/TerrainSurface';
import { clear, el, setClass } from './dom';
import { icon } from './Icons';
import { describeLayerOrigin } from './TextureStudio';

/**
 * Tool options for the ground tools — the tray panel that appears while paint or sculpt is live.
 *
 * Built once and *updated* rather than re-rendered, unlike the panels that rebuild their body on
 * every refresh. Those panels can afford it because nothing in them is dragged; this one is nothing
 * but sliders, and replacing an `<input type=range>` while the pointer is down on it silently ends
 * the drag. The brush size control that stops responding halfway through a change is exactly the
 * kind of small betrayal that makes a tool feel unreliable.
 *
 * Brush radius is on a logarithmic slider. Linear from 0.1 m to 25 m would spend nine tenths of its
 * travel on sizes larger than any bed edge, leaving the useful range in the first two millimetres of
 * the track. Logarithmic gives equal travel to equal *proportional* change, which is how brush size
 * is actually thought about — "a bit bigger", not "0.4 m bigger".
 */

export interface TerrainPanelDeps {
  editor: TerrainEditor;
  /**
   * Where the panel mounts while a ground tool is live. It is attached and detached rather than
   * merely hidden, so that the dock's "first panel fills the slack" rule keeps meaning the panel you
   * are actually working in — a hidden element is still a first child.
   */
  host: () => HTMLElement;
  /** Fraction of the site under each material, for the coverage readout. Null when no terrain. */
  coverage: () => SurfaceCoverage | null;
  /** Called after a control changes something, so the rest of the HUD can follow. */
  onChange: () => void;
  /**
   * Open the dialog that makes a new material. Omitted hides the button entirely, rather than
   * showing a dead one — the panel is usable without the forge, it simply has five materials.
   */
  onNewMaterial?: () => void;
  /**
   * Retire a material this site added. The panel asks; the caller decides what to do about the
   * paint that used it, because taking weights out of the ground is an undoable edit and the panel
   * has no business owning the history.
   */
  onRetireMaterial?: (layer: SurfaceLayer) => void;
  /** Which unit the contour readout is written in. */
  displayUnit?: () => UnitSystem;
  /** Whether the topographic overlay is drawn. Omitted hides the whole contour section. */
  topoVisible?: () => boolean;
  onToggleTopo?: () => void;
  topoLabelMode?: () => ContourLabelMode;
  onCycleTopoLabels?: () => void;
  /** Vertical spacing between contours, in metres. */
  topoIntervalMeters?: () => number;
  /** Spacing between the numbered contours, or zero when every line is numbered. */
  topoIndexIntervalMeters?: () => number;
}

/** What each label mode does, said in the panel rather than left to be discovered. */
const LABEL_MODE_COPY: Record<ContourLabelMode, { label: string; hint: string }> = {
  auto: { label: 'Auto', hint: 'Elevations appear as you get close enough to read them' },
  always: { label: 'Always', hint: 'Every contour keeps its elevation, at any zoom' },
  off: { label: 'Off', hint: 'Lines only — no numbers over the garden' },
};

interface Slider {
  root: HTMLElement;
  input: HTMLInputElement;
  value: HTMLElement;
}

export class TerrainPanel {
  readonly root: HTMLElement;

  private readonly title: HTMLElement;
  private readonly headIcon: HTMLElement;
  private readonly body: HTMLElement;
  private readonly paintSection: HTMLElement;
  private readonly sculptSection: HTMLElement;
  private readonly materialList = el('div', { class: 'lg-materials' });
  private readonly layerButtons = new Map<SurfaceLayerId, HTMLButtonElement>();
  /** Palette revision the material list was built from, so it is rebuilt only when it changed. */
  private materialsRevision = -1;
  private readonly modeButtons = new Map<string, HTMLButtonElement>();
  private readonly coverageList: HTMLElement;
  private readonly coverageTitle: HTMLElement;
  private readonly topoSection: HTMLElement | null;
  private readonly topoTitle: HTMLElement;
  private readonly topoToggle: HTMLButtonElement;
  private readonly topoInterval: HTMLElement;
  private readonly labelButtons = new Map<ContourLabelMode, HTMLButtonElement>();
  private readonly radius: Slider;
  private readonly hardness: Slider;
  private readonly strength: Slider;
  private readonly opacity: Slider;

  constructor(private readonly deps: TerrainPanelDeps) {
    this.headIcon = el('span', { class: 'lg-panel__icon', html: icon('paint', 16) });
    this.title = el('h2', { class: 'lg-panel__title', text: 'Ground paint' });

    this.paintSection = this.buildPaintSection();
    this.sculptSection = this.buildSculptSection();
    this.coverageList = el('dl', { class: 'lg-coverage' });
    this.coverageTitle = el('h3', { class: 'lg-section__title', text: 'Ground cover' });

    this.topoTitle = el('h3', { class: 'lg-section__title', text: 'Contour map' });
    // The legend gets a line of its own rather than a value pinned to the right of a label: it is a
    // sentence about the drawing, and at 46 m of relief in feet it is a long one.
    this.topoInterval = el('p', { class: 'lg-legend', text: '' });
    this.topoToggle = el('button', {
      class: 'lg-button lg-button--toggle',
      attrs: { type: 'button', 'aria-pressed': false },
      text: 'Show contours',
      on: {
        click: () => {
          this.deps.onToggleTopo?.();
          this.deps.onChange();
          this.refresh();
        },
      },
    });
    this.topoSection = this.buildTopoSection();

    this.radius = this.slider('Brush size', 0, 1, 0.001, (t) =>
      this.deps.editor.setBrushRadius(fromLog(t, MIN_BRUSH_RADIUS, MAX_BRUSH_RADIUS)),
    );
    this.hardness = this.slider('Edge', 0, 1, 0.01, (v) => this.deps.editor.setBrushHardness(v));
    this.strength = this.slider('Strength', 0, 1, 0.001, (t) => this.applyStrength(t));
    this.opacity = this.slider('Max coverage', 0.05, 1, 0.01, (v) =>
      this.deps.editor.setPaintOpacity(v),
    );

    this.body = el(
      'div',
      { class: 'lg-panel__body' },
      this.paintSection,
      this.sculptSection,
      el('h3', { class: 'lg-section__title', text: 'Brush' }),
      this.radius.root,
      this.hardness.root,
      this.strength.root,
      this.opacity.root,
      this.coverageTitle,
      this.coverageList,
      this.topoTitle,
      this.topoSection,
      this.buildHints(),
    );

    this.root = el(
      'aside',
      { class: 'lg-panel lg-panel--right lg-panel--tool', attrs: { 'aria-label': 'Ground tool' } },
      el('div', { class: 'lg-panel__head' }, this.headIcon, this.title),
      this.body,
    );
  }

  /* --------------------------------- Sections --------------------------------- */

  /**
   * The material list.
   *
   * Rebuilt on demand rather than once at construction, unlike everything else in this panel: the
   * palette is no longer fixed, and a forged material has to appear the moment it exists. The
   * sliders keep their build-once treatment because they are dragged and this list is not.
   */
  private buildPaintSection(): HTMLElement {
    const section = el('div', { class: 'lg-tool-section' }, this.materialList);
    this.rebuildMaterials();
    return section;
  }

  private rebuildMaterials(): void {
    clear(this.materialList);
    this.layerButtons.clear();

    // Grass leads the list because it is the eraser, and a paint tool whose eraser is hidden behind
    // a modifier is a paint tool people ask for an undo button instead of using.
    for (const layer of surfaceLayers()) {
      const button = el(
        'button',
        {
          class: 'lg-material',
          title: [layer.hint, describeLayerOrigin(layer)].filter(Boolean).join('\n'),
          attrs: { type: 'button', 'aria-pressed': false },
          on: {
            click: () => {
              this.deps.editor.setPaintLayer(layer.id);
              this.deps.onChange();
              this.refresh();
            },
          },
        },
        materialChip(layer),
        el('span', { class: 'lg-material__name', text: layer.name }),
        layer.plantable
          ? null
          : el('span', { class: 'lg-material__flag', text: 'no planting', title: 'Plants are refused here' }),
      );
      this.layerButtons.set(layer.id, button);

      // A custom material gets its retire control inside its own row, so the association is never
      // in doubt — a separate "remove a material" list would be one more place to pick the wrong one.
      if (!layer.builtIn && this.deps.onRetireMaterial) {
        this.materialList.appendChild(
          el(
            'div',
            { class: 'lg-material-row' },
            button,
            el('button', {
              class: 'lg-material__retire',
              html: icon('delete', 14),
              title: `Retire ${layer.name} — the paint that used it is taken back out of the ground`,
              attrs: { type: 'button', 'aria-label': `Retire ${layer.name}` },
              on: {
                click: (event) => {
                  event.stopPropagation();
                  this.deps.onRetireMaterial?.(layer);
                  this.refresh();
                },
              },
            }),
          ),
        );
      } else {
        this.materialList.appendChild(button);
      }
    }

    if (!this.deps.onNewMaterial) return;
    const full = surfacePalette.freeSlots <= 0;
    const add = el(
      'button',
      {
        class: 'lg-material lg-material--new',
        title: full
          ? 'Every painted slot on this site is taken. Retire a material to make room.'
          : 'Describe a material and have it made, or upload a swatch of your own',
        attrs: { type: 'button' },
        on: { click: () => this.deps.onNewMaterial?.() },
      },
      el('span', { class: 'lg-material__chip lg-material__chip--new', html: icon('plus', 14) }),
      el('span', { class: 'lg-material__name', text: 'New material…' }),
    );
    add.disabled = full;
    this.materialList.appendChild(add);
  }

  private buildSculptSection(): HTMLElement {
    const segment = el('div', { class: 'lg-segment', attrs: { role: 'group', 'aria-label': 'Sculpt mode' } });
    for (const mode of SCULPT_MODES) {
      const button = el('button', {
        class: 'lg-segment__item',
        text: sculptModeLabel(mode),
        attrs: { type: 'button', 'aria-pressed': false },
        on: {
          click: () => {
            this.deps.editor.setSculptMode(mode);
            this.deps.onChange();
            this.refresh();
          },
        },
      });
      this.modeButtons.set(mode, button);
      segment.appendChild(button);
    }
    return el('div', { class: 'lg-tool-section' }, segment);
  }

  /**
   * The contour map's controls, shown while sculpting.
   *
   * Absent entirely when the app did not wire an overlay, rather than present and inert: a dead
   * switch in a tool panel is a bug report waiting to happen. The label modes are a segmented
   * control for the same reason the sculpt modes are — three named states you pick between, not a
   * thing you cycle blindly and then have to look at the garden to identify.
   */
  private buildTopoSection(): HTMLElement | null {
    if (!this.deps.onToggleTopo || !this.deps.topoVisible) return null;

    const segment = el('div', {
      class: 'lg-segment',
      attrs: { role: 'group', 'aria-label': 'Elevation figures' },
    });
    if (this.deps.onCycleTopoLabels && this.deps.topoLabelMode) {
      for (const mode of CONTOUR_LABEL_MODES) {
        const copy = LABEL_MODE_COPY[mode];
        const button = el('button', {
          class: 'lg-segment__item',
          text: copy.label,
          title: copy.hint,
          attrs: { type: 'button', 'aria-pressed': false },
          on: { click: () => this.setLabelMode(mode) },
        });
        this.labelButtons.set(mode, button);
        segment.appendChild(button);
      }
    }

    return el(
      'div',
      { class: 'lg-tool-section' },
      this.topoToggle,
      this.labelButtons.size > 0 ? segment : null,
      // The legend reads as a caption under the controls rather than a labelled field above them:
      // it is the one line here that reports rather than asks, and the panel is short on room.
      this.topoInterval,
    );
  }

  /**
   * Set the label mode by name.
   *
   * The overlay only offers a cycle — one button on the toolbar, one keystroke — so a named pick
   * here is that cycle turned until it lands. Bounded by the mode count, so a mode the panel does
   * not know about cannot spin it forever.
   */
  private setLabelMode(mode: ContourLabelMode): void {
    for (let i = 0; i < CONTOUR_LABEL_MODES.length; i += 1) {
      if (this.deps.topoLabelMode?.() === mode) break;
      this.deps.onCycleTopoLabels?.();
    }
    this.deps.onChange();
    this.refresh();
  }

  private buildHints(): HTMLElement {
    return el(
      'div',
      { class: 'lg-hint lg-hint--keys' },
      el('p', { text: 'Drag to apply · middle-drag orbits, so you can keep working while you turn.' }),
      el('p', { html: '<b>Alt</b> inverse · <b>Shift</b> smooth' }),
      el('p', { html: '<b>Ctrl</b>+wheel or <b>[ ]</b> size · <b>Shift</b>+wheel or <b>Shift</b>+<b>[ ]</b> strength' }),
      el('p', { html: '<b>-</b> <b>=</b> edge softness · <b>,</b> <b>.</b> cycle · <b>Esc</b> to finish' }),
    );
  }

  private slider(
    label: string,
    min: number,
    max: number,
    step: number,
    onInput: (value: number) => void,
  ): Slider {
    const value = el('span', { class: 'lg-field__value', text: '' });
    const input = el('input', {
      class: 'lg-slider',
      attrs: {
        type: 'range',
        min: String(min),
        max: String(max),
        step: String(step),
        value: String(min),
        'aria-label': label,
      },
      on: {
        input: (event) => {
          onInput(Number((event.target as HTMLInputElement).value));
          this.syncValues();
          this.deps.onChange();
        },
      },
    });
    const root = el(
      'div',
      { class: 'lg-control' },
      el('div', { class: 'lg-control__head' }, el('span', { class: 'lg-field__label', text: label }), value),
      input,
    );
    return { root, input, value };
  }

  private applyStrength(t: number): void {
    if (this.deps.editor.activeTool === 'sculpt') {
      this.deps.editor.setBrushStrength(fromLog(t, MIN_SCULPT_STRENGTH, MAX_SCULPT_STRENGTH));
    } else {
      this.deps.editor.setBrushStrength(Math.max(0.02, t));
    }
  }

  /* ---------------------------------- Refresh ---------------------------------- */

  /** Mount, unmount, and re-read the panel from the tool. Cheap enough to call every HUD refresh. */
  refresh(): void {
    const tool: TerrainToolKind | null = this.deps.editor.activeTool;
    const host = this.deps.host();
    if (tool === null) {
      this.root.remove();
      setClass(host, 'has-tool', false);
      return;
    }
    if (!this.root.isConnected) host.prepend(this.root);
    setClass(host, 'has-tool', true);

    const painting = tool === 'paint';
    this.title.textContent = painting ? 'Ground paint' : 'Sculpt terrain';
    this.headIcon.innerHTML = icon(painting ? 'paint' : 'sculpt', 16);
    this.paintSection.hidden = !painting;
    this.sculptSection.hidden = painting;
    this.opacity.root.hidden = !painting;
    this.coverageTitle.hidden = !painting;
    this.coverageList.hidden = !painting;
    // Only while sculpting: painting a bed is not a question about elevation, and the contour map is
    // still one toolbar button away for the rare time it is.
    if (this.topoSection) {
      this.topoSection.hidden = painting;
      this.topoTitle.hidden = painting;
      if (!painting) this.refreshTopo();
    }

    if (painting && this.materialsRevision !== surfacePalette.revision) {
      this.materialsRevision = surfacePalette.revision;
      this.rebuildMaterials();
    }

    for (const [id, button] of this.layerButtons) {
      const active = painting && this.deps.editor.paint.layer === id;
      setClass(button, 'is-active', active);
      button.setAttribute('aria-pressed', String(active));
    }
    for (const [mode, button] of this.modeButtons) {
      const active = !painting && this.deps.editor.sculpt.mode === mode;
      setClass(button, 'is-active', active);
      button.setAttribute('aria-pressed', String(active));
    }

    this.syncValues();
    if (painting) this.refreshCoverage();
  }

  /** Push the tool's current numbers into the sliders, skipping any the user is dragging. */
  private syncValues(): void {
    const editor = this.deps.editor;
    const brush = editor.activeBrush;
    const sculpting = editor.activeTool === 'sculpt';

    this.setSlider(this.radius, toLog(brush.radiusMeters, MIN_BRUSH_RADIUS, MAX_BRUSH_RADIUS));
    this.radius.value.textContent = `${brush.radiusMeters.toFixed(brush.radiusMeters < 1 ? 2 : 1)} m`;

    this.setSlider(this.hardness, brush.hardness);
    this.hardness.value.textContent = `${Math.round(brush.hardness * 100)}% hard`;

    if (sculpting) {
      this.setSlider(
        this.strength,
        toLog(editor.sculpt.strengthMeters, MIN_SCULPT_STRENGTH, MAX_SCULPT_STRENGTH),
      );
      this.strength.value.textContent = `${(editor.sculpt.strengthMeters * 100).toFixed(1)} cm / dab`;
    } else {
      this.setSlider(this.strength, editor.paint.brush.flow);
      this.strength.value.textContent = `${Math.round(editor.paint.brush.flow * 100)}% flow`;
      this.setSlider(this.opacity, editor.paint.brush.opacity);
      this.opacity.value.textContent = `${Math.round(editor.paint.brush.opacity * 100)}%`;
    }
  }

  private setSlider(slider: Slider, value: number): void {
    // Never fight the user's own drag: a value written back into the input they are holding would
    // snap the thumb out from under the pointer.
    if (document.activeElement === slider.input) return;
    slider.input.value = String(value);
  }

  private refreshTopo(): void {
    const visible = this.deps.topoVisible?.() === true;
    setClass(this.topoToggle, 'is-active', visible);
    this.topoToggle.setAttribute('aria-pressed', String(visible));
    this.topoToggle.textContent = visible ? 'Contours on' : 'Show contours';

    const interval = this.deps.topoIntervalMeters?.() ?? 0;
    const numbered = this.deps.topoIndexIntervalMeters?.() ?? 0;
    const unit = this.deps.displayUnit?.() ?? 'metric';
    // The map's legend. Both numbers are chosen for you from the site's relief, so they are reported
    // rather than set — and without them a run of close lines is a shape rather than a grade.
    this.topoInterval.textContent = !visible || interval <= 0
      ? 'Lines appear once the ground has some fall to it.'
      : numbered > 0
        ? `Lines every ${formatElevation(interval, unit, interval)}, numbered every ${formatElevation(numbered, unit, interval)}.`
        : `Lines every ${formatElevation(interval, unit, interval)}, every one numbered.`;

    const mode = this.deps.topoLabelMode?.() ?? 'auto';
    for (const [candidate, button] of this.labelButtons) {
      const active = candidate === mode;
      setClass(button, 'is-active', active);
      button.setAttribute('aria-pressed', String(active));
      button.disabled = !visible;
    }
  }

  private refreshCoverage(): void {
    const coverage = this.deps.coverage();
    clear(this.coverageList);
    if (!coverage) return;
    for (const layer of surfaceLayers()) {
      const fraction = coverage[layer.id] ?? 0;
      if (fraction < 0.005 && layer.channel >= 0) continue;
      this.coverageList.appendChild(
        el(
          'div',
          { class: 'lg-coverage__row' },
          materialChip(layer),
          el('dt', { class: 'lg-field__label', text: layer.name }),
          el('dd', { class: 'lg-field__value', text: `${(fraction * 100).toFixed(fraction < 0.1 ? 1 : 0)}%` }),
        ),
      );
    }
  }

  dispose(): void {
    this.layerButtons.clear();
    this.modeButtons.clear();
    this.labelButtons.clear();
    this.root.remove();
  }
}

/**
 * The colour square beside a material's name.
 *
 * Shows the swatch itself when there is one, at a tile size chosen so the chip reads as *material*
 * rather than as a crop — one tile across a 16-pixel square is a blur, four is recognisably gravel.
 * Falls back to the flat average colour, which is also what the ground renders while a swatch is
 * still decoding, so the chip and the garden never disagree.
 */
function materialChip(layer: SurfaceLayer): HTMLElement {
  if (!layer.swatch) {
    return el('span', { class: 'lg-material__chip', style: { background: layer.color } });
  }
  return el('span', {
    class: 'lg-material__chip lg-material__chip--swatch',
    style: {
      backgroundColor: layer.color,
      backgroundImage: `url(${layer.swatch.dataUrl})`,
      backgroundSize: '50% 50%',
    },
  });
}

/** Value → 0–1 slider position on a logarithmic scale. */
function toLog(value: number, min: number, max: number): number {
  const clamped = Math.min(max, Math.max(min, value));
  return Math.log(clamped / min) / Math.log(max / min);
}

/** 0–1 slider position → value on a logarithmic scale. The inverse of {@link toLog}. */
function fromLog(t: number, min: number, max: number): number {
  return min * Math.pow(max / min, Math.min(1, Math.max(0, t)));
}
